/** @jsxRuntime classic */
/** @jsx jsxPragma */

import { getErrorFromVideoEvent } from "@outschool/backpack";
import { SystemStyleObject } from "@styled-system/css";
import React from "react";

import jsxPragma from "../jsxPragma";

export type VideoProps = React.VideoHTMLAttributes<HTMLVideoElement> & {
  sx?: SystemStyleObject;
  onVideoCanPlay?: () => void;
  onVideoError?: (error: Error) => void;
  videoRef?: React.MutableRefObject<HTMLVideoElement | null>;
};

/** A video element tag which accepts sx props */
export default function Video({
  sx,
  onVideoCanPlay,
  onVideoError,
  videoRef,
  ...props
}: VideoProps) {
  const [node, setNode] = React.useState<HTMLVideoElement | null>(null);
  React.useEffect(() => {
    if (!node) {
      return undefined;
    }

    const canPlayHandler = () => {
      if (onVideoCanPlay) {
        onVideoCanPlay();
      }
    };

    // Prefer getErrorFromVideoEvent over console.error(event): CaptureConsole
    // turns a raw DOM Event into Sentry "Error: [object Event]".
    const errorHandler = (event: Event) => {
      onVideoError?.(getErrorFromVideoEvent(event));
    };

    node.addEventListener("canplay", canPlayHandler);
    node.addEventListener("error", errorHandler);

    return () => {
      node.removeEventListener("canplay", canPlayHandler);
      node.removeEventListener("error", errorHandler);
    };
  }, [node, onVideoCanPlay, onVideoError]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={node => {
        setNode(node);
        if (videoRef) {
          videoRef.current = node;
        }
      }}
      // @ts-ignore
      // eslint-disable-next-line
      sx={sx}
      data-testid="video"
      data-test-id="video"
      {...props}
    />
  );
}
